import React, { useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import { getTheme } from "../lib/themes";

const EXT_TO_MONACO = {
  py: "python", js: "javascript", mjs: "javascript", cjs: "javascript",
  ts: "typescript", tsx: "typescript", jsx: "javascript",
  html: "html", htm: "html", css: "css", scss: "scss", json: "json",
  md: "markdown", java: "java", c: "c", h: "c", cpp: "cpp", cc: "cpp",
  hpp: "cpp", go: "go", rs: "rust", php: "php", rb: "ruby", pl: "perl",
  lua: "lua", sh: "shell", bash: "shell", sql: "sql", swift: "swift",
  kt: "kotlin", cs: "csharp", r: "r", dart: "dart", txt: "plaintext",
  yml: "yaml", yaml: "yaml", xml: "xml",
};

export function monacoLangForPath(path, fallback = "plaintext") {
  if (!path) return fallback;
  const m = path.toLowerCase().match(/\.([a-z0-9]+)$/);
  if (m && EXT_TO_MONACO[m[1]]) return EXT_TO_MONACO[m[1]];
  return fallback;
}

export default function EditorPane({
  path,
  value,
  monacoLanguage,
  onChange,
  onRequestRun,
  themeId = "brand",
}) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const containerRef = useRef(null);
  const onRequestRunRef = useRef(onRequestRun);
  useEffect(() => { onRequestRunRef.current = onRequestRun; }, [onRequestRun]);

  // Re-apply theme whenever themeId changes (after first mount).
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;
    try {
      const t = getTheme(themeId);
      monaco.editor.defineTheme("runbox-" + themeId, {
        base: t.monaco.base || "vs-dark",
        inherit: true,
        rules: t.monaco.tokens || [],
        colors: t.monaco.colors || {},
      });
      monaco.editor.setTheme("runbox-" + themeId);
    } catch (_) { /* */ }
  }, [themeId]);

  // Drive layout manually via ResizeObserver instead of Monaco's
  // `automaticLayout: true` polling. The polling implementation inside
  // Monaco occasionally dereferences `viewLayout.getCurrentViewport()` before
  // the render-context is ready, which throws
  // "Cannot read properties of undefined (reading 'dimensions')" when the
  // editor is first mounted on a recently-resized page. A manual
  // ResizeObserver + rAF doesn't have that race.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return () => {};
    let frame = 0;
    const doLayout = () => {
      frame = 0;
      const ed = editorRef.current;
      if (!ed) return;
      const r = node.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        try { ed.layout({ width: r.width, height: r.height }); } catch (_) { /* */ }
      }
    };
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(doLayout);
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(schedule);
      ro.observe(node);
      return () => {
        if (frame) cancelAnimationFrame(frame);
        try { ro.disconnect(); } catch (_) { /* */ }
      };
    }
    // Fallback: listen to window resize
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  function handleMount(editor, monaco) {
    editorRef.current = editor;
    monacoRef.current = monaco;
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
      () => onRequestRunRef.current?.()
    );
    // Register all themes so theme switching is instant afterwards.
    const t = getTheme(themeId);
    monaco.editor.defineTheme("runbox-" + themeId, {
      base: t.monaco.base || "vs-dark",
      inherit: true,
      rules: t.monaco.tokens || [],
      colors: t.monaco.colors || {},
    });
    monaco.editor.setTheme("runbox-" + themeId);
    requestAnimationFrame(() => {
      const node = containerRef.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        try { editor.layout({ width: r.width, height: r.height }); } catch (_) { /* */ }
      }
    });
  }

  if (!path) {
    return (
      <div className="flex-1 grid place-items-center text-ide-mute text-sm bg-ide-panel2">
        Open or create a file to start editing.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full bg-ide-panel2 overflow-hidden">
      <Editor
        path={path}
        theme="vs-dark"
        language={monacoLanguage || "plaintext"}
        value={value}
        onMount={handleMount}
        onChange={(v) => onChange?.(v ?? "")}
        options={{
          fontFamily:
            "JetBrains Mono, Fira Code, SF Mono, Menlo, Consolas, monospace",
          fontLigatures: true,
          fontSize: 14,
          lineHeight: 1.65,
          minimap: { enabled: true, scale: 1, maxColumn: 80, renderCharacters: false },
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          cursorBlinking: "smooth",
          cursorSmoothCaretAnimation: "on",
          renderLineHighlight: "all",
          tabSize: 2,
          automaticLayout: false,
          padding: { top: 14, bottom: 14 },
          scrollbar: {
            verticalScrollbarSize: 8,
            horizontalScrollbarSize: 8,
            useShadows: false,
          },
          // Advanced editor features
          bracketPairColorization: { enabled: true, independentColorPoolPerBracketType: true },
          guides: { bracketPairs: "active", indentation: true },
          formatOnPaste: true,
          formatOnType: true,
          autoClosingBrackets: "always",
          autoClosingQuotes: "always",
          autoSurround: "languageDefined",
          autoIndent: "full",
          parameterHints: { enabled: true, cycle: true },
          suggest: {
            preview: true,
            showKeywords: true,
            showSnippets: true,
            showClasses: true,
            showFunctions: true,
            showMethods: true,
            showVariables: true,
            insertMode: "insert",
            filterGraceful: true,
          },
          inlineSuggest: { enabled: true },
          quickSuggestions: { other: true, comments: false, strings: true },
          acceptSuggestionOnEnter: "on",
          tabCompletion: "on",
          wordBasedSuggestions: "currentDocument",
          folding: true,
          foldingStrategy: "auto",
          showFoldingControls: "mouseover",
          stickyScroll: { enabled: true, maxLineCount: 5 },
          hover: { enabled: true, delay: 400 },
          lightbulb: { enabled: "on" },
          multiCursorModifier: "alt",
          linkedEditing: true,
          find: { addExtraSpaceOnTop: false },
          accessibilitySupport: "off",
          renderValidationDecorations: "on",
          occurrencesHighlight: "singleFile",
        }}
      />
    </div>
  );
}
